import { cn } from "../../shared/utils";
import { ScrollArea } from "../ui/scroll-area";

interface BgContentWrapProps {
    children: React.ReactNode;
    customStyles?: string;
    useScrollArea?: boolean;
    transparent?: boolean;
}

/**
 * BgContentWrap - Main content wrapper for authenticated pages
 *
 * Architecture Pattern:
 * - Uses ecotic-bg-content-wrap utility class (single source of truth)
 * - ScrollArea: Handles vertical scrolling with magenta scrollbar
 * - Content div: Renders form/page content
 *
 * Provides:
 * - Design system styling: lime border, white/40 overlay (via utility class)
 * - Scrollable content: Uses shadcn ScrollArea with visible magenta scrollbar
 * - Clean single-box layout: No nested containers
 * - Mouse wheel scrolling: Enabled via overflow-y: auto
 * - Mobile responsive: Compact padding on mobile via padding-responsive utility
 *
 * All 14+ pages using BgContentWrap get consistent scrolling automatically.
 */
export default function BgContentWrap({ children, customStyles, useScrollArea = true, transparent = false }: BgContentWrapProps) {
    return (
        <div className={cn(
            "ecotic-bg-content-wrap padding-responsive",
            transparent && "!border-0 !bg-transparent",
            customStyles
        )}>
            {useScrollArea ? (
                <ScrollArea
                    id="main-scrollable-content"
                    className="ecotic-scroll-container ecotic-form-scrollbar"
                >
                    <div className="flex w-full flex-col pr-2 md:pr-4">
                        {children}
                    </div>
                </ScrollArea>
            ) : (
                <div className="flex w-full flex-col flex-1 min-h-0 overflow-hidden">
                    {children}
                </div>
            )}
        </div>
    );
}
